'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  BookOpen,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  FileText,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { formatDrugForm } from '../../lib/drug-form';
import { formatDate, formatDateShort } from '../../lib/format-date';
import type { ChplQuote } from '../../types';
import { PdfViewer } from '../pdf-viewer';
import { Chapter45Content } from './chapter45-content';
import { useHighlightNavigation } from './hooks/use-highlight-navigation';
import { extractProductId, hasPdfContent } from './utils';

export type ViewMode = 'markdown' | 'pdf';
export type DocumentType = 'chpl' | 'ulotka';
export { hasPdfContent };

// ============================================================================
// Helper Components
// ============================================================================

function CopyDrugId({ id }: { id: string }) {
  const { t } = useTranslation('interactions');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'ml-1 inline-flex shrink-0 items-center justify-center rounded p-0.5 transition-colors',
        copied
          ? 'text-green-500'
          : 'text-transparent group-hover/header:text-zinc-300 group-hover/header:hover:bg-zinc-100 group-hover/header:hover:text-zinc-500 dark:group-hover/header:text-zinc-600 dark:group-hover/header:hover:bg-zinc-700/50 dark:group-hover/header:hover:text-zinc-400',
      )}
      title={t('viewer.copyId', { id })}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function PdfViewerWithInitialPage({
  fileUrl,
  initialPage,
  downloadUrl,
}: {
  fileUrl: string;
  initialPage: number;
  downloadUrl?: string;
}) {
  const [savedInitialPage] = useState(() => initialPage);

  return (
    <PdfViewer
      fileUrl={fileUrl}
      initialPage={savedInitialPage}
      downloadUrl={downloadUrl}
    />
  );
}

// ============================================================================
// Main Viewer Inner Component
// ============================================================================

interface ChplViewerInnerProps {
  quote: ChplQuote;
  onClose: () => void;
  initialViewMode?: ViewMode;
  /** When true, scroll/navigate to chapter 4.5 (e.g. from quote). When false, start at beginning (e.g. from drug list). */
  scrollToChapter45?: boolean;
  onViewModeChange?: (mode: ViewMode) => void;
  /** Document type: 'chpl' for ChPL (default), 'ulotka' for patient leaflet (PDF-only) */
  documentType?: DocumentType;
  /** Whether this drug has a ChPL PDF available */
  hasChplPdf?: boolean;
  /** Whether this drug has an ulotka (patient leaflet) available */
  hasUlotka?: boolean;
  /** Pre-known extraction availability from drug metadata (avoids waiting for fetch) */
  hasChplExtraction?: boolean;
}

function ChplViewerInner({
  quote,
  onClose,
  initialViewMode = 'markdown',
  scrollToChapter45 = false,
  onViewModeChange,
  documentType: initialDocumentType = 'chpl',
  hasChplPdf = true,
  hasUlotka = false,
  hasChplExtraction,
}: ChplViewerInnerProps) {
  const { t, i18n } = useTranslation('interactions');
  const language = i18n.language;
  const [hasMarkdownContent, setHasMarkdownContent] = useState<boolean | null>(
    hasChplExtraction ?? null,
  );
  const [documentType, setDocumentType] =
    useState<DocumentType>(initialDocumentType);
  const [viewMode, setViewModeInternal] = useState<ViewMode>(initialViewMode);

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setViewModeInternal(mode);
      onViewModeChange?.(mode);
    },
    [onViewModeChange],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const markdownScrollRef = useRef<HTMLDivElement>(null);

  const quoteCount = useMemo(() => {
    return quote.texts?.filter((t) => t.trim().length > 0).length ?? 0;
  }, [quote.texts]);

  const productId = useMemo(() => {
    return quote.url ? extractProductId(quote.url) : null;
  }, [quote.url]);

  const chapter45Id = quote.chplId ?? quote.drugId ?? productId;

  const isUlotka = documentType === 'ulotka';
  const apiPrefix = isUlotka ? '/api/ulotka' : '/api/chpl';

  const pdfUrl = useMemo(() => {
    if (quote.drugId) {
      return `${apiPrefix}/${quote.drugId}`;
    }
    if (productId) {
      return `${apiPrefix}/${productId}`;
    }
    return null;
  }, [quote.drugId, productId, apiPrefix]);

  const chapter45Anchors = useMemo(
    () => quote.chapter45Anchors ?? [],
    [quote.chapter45Anchors],
  );

  const [chapter45Content, setChapter45Content] = useState<string | null>(null);
  const [isChapter45Loading, setIsChapter45Loading] = useState(true);
  const [documentUpdatedAt, setDocumentUpdatedAt] = useState<string | null>(
    null,
  );
  const [lastSyncCheckDate, setLastSyncCheckDate] = useState<string | null>(
    null,
  );

  const {
    activeQuoteIndex,
    visibleQuoteCount,
    focusQuoteAtIndex,
    scrollToFirstHighlight,
    hasScrolledToHighlight,
  } = useHighlightNavigation(markdownScrollRef, {
    quoteCount,
    anchors: chapter45Anchors,
  });

  // Fetch extracted chapter 4.5 content on mount (always for ChPL, regardless of current tab)
  useEffect(() => {
    if (!chapter45Id) {
      setIsChapter45Loading(false);
      setHasMarkdownContent(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    setIsChapter45Loading(true);
    setChapter45Content(null);

    void (async () => {
      try {
        // `x-lang` lets the server serve the static English demo chapter for
        // the example drugs when the viewer runs under `/en`.
        const res = await fetch(`/api/chpl/${chapter45Id}/chapter-45`, {
          signal: controller.signal,
          headers: { 'x-lang': language },
        });

        const data = (await res.json()) as {
          content: string | null;
        };
        if (!cancelled) {
          const content = data.content ?? null;
          setChapter45Content(content);
          const isAvailable = Boolean(content);
          setHasMarkdownContent(isAvailable);
          if (!isAvailable) {
            setViewMode('pdf');
          }
        }
      } catch {
        if (!cancelled) {
          setChapter45Content(null);
          setHasMarkdownContent(false);
          setViewMode('pdf');
        }
      } finally {
        if (!cancelled) setIsChapter45Loading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter45Id, language]);

  // Fetch publication date for the current document type (ChPL or Ulotka)
  const drugIdForPubDate = quote.drugId ?? productId;
  useEffect(() => {
    if (!drugIdForPubDate) return;

    let cancelled = false;
    const controller = new AbortController();
    const type = documentType === 'ulotka' ? 'leaflet' : 'chpl';

    void (async () => {
      try {
        const res = await fetch(
          `/api/chpl/${drugIdForPubDate}/publication-date?type=${type}`,
          { signal: controller.signal },
        );
        const data = (await res.json()) as {
          publicationDate?: string | null;
          lastSyncCheckDate?: string | null;
        };
        if (!cancelled) {
          setDocumentUpdatedAt(data.publicationDate ?? null);
          setLastSyncCheckDate(data.lastSyncCheckDate ?? null);
        }
      } catch {
        // Keep existing values on error
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [drugIdForPubDate, documentType]);

  // Scroll to first highlight once (only when opened from a quote with scrollToChapter45)
  useEffect(() => {
    if (!scrollToChapter45) return;
    if (viewMode !== 'markdown') return;
    if (hasScrolledToHighlight.current) return;
    if (chapter45Anchors.length === 0) return;
    if (isChapter45Loading) return;
    if (!chapter45Content) return;

    const timeoutId = setTimeout(() => {
      if (scrollToFirstHighlight()) {
        hasScrolledToHighlight.current = true;
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [
    scrollToChapter45,
    chapter45Anchors.length,
    chapter45Content,
    isChapter45Loading,
    viewMode,
    scrollToFirstHighlight,
    hasScrolledToHighlight,
  ]);

  // Fetch initial page for PDF
  // ChPL: chapter 4.5 page from extraction data
  // Ulotka: leaflet_start_page from drug record (for CEN combined annex PDFs)
  const drugIdForPage = quote.drugId ?? productId;
  const [initialPage, setInitialPage] = useState<number>(1);

  useEffect(() => {
    if (!drugIdForPage) return;

    setInitialPage(1);
    let cancelled = false;
    const controller = new AbortController();

    const endpoint = isUlotka
      ? `/api/ulotka/${drugIdForPage}/page`
      : `/api/chpl/${drugIdForPage}/page`;

    void (async () => {
      try {
        const res = await fetch(endpoint, { signal: controller.signal });
        const data = (await res.json()) as { page?: number };
        if (!cancelled && data.page) {
          setInitialPage(data.page);
        }
      } catch {
        // Keep default on error
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [drugIdForPage, isUlotka]);

  const initialPdfPage = scrollToChapter45 ? initialPage : 1;

  // Determine which switcher tab is active
  const activeTab =
    documentType === 'ulotka'
      ? 'ulotka-pdf'
      : viewMode === 'markdown'
        ? 'chpl-45'
        : 'chpl-pdf';

  // Count available tabs to decide whether to show switcher
  // Show markdown tab while loading (optimistic) — hide only if confirmed unavailable
  const showMarkdownTab = hasMarkdownContent !== false;
  const showChplPdfTab = hasChplPdf;
  const showUlotkaTab = hasUlotka;
  const tabCount =
    (showMarkdownTab ? 1 : 0) +
    (showChplPdfTab ? 1 : 0) +
    (showUlotkaTab ? 1 : 0);

  return (
    <div className="flex h-full flex-col">
      {/* 3-way document switcher */}
      {tabCount > 1 && (
        <div className="mb-1.5 flex items-center justify-center gap-1 px-1 sm:gap-3 sm:px-2">
          {[
            {
              id: 'chpl-pdf',
              show: showChplPdfTab,
              icon: FileText,
              label: t('viewer.tabChpl'),
              secondary: t('viewer.tabPdf'),
              onClick: () => {
                setDocumentType('chpl');
                setViewMode('pdf');
              },
            },
            {
              id: 'ulotka-pdf',
              show: showUlotkaTab,
              icon: FileText,
              label: t('viewer.tabLeaflet'),
              secondary: t('viewer.tabPdf'),
              onClick: () => {
                setDocumentType('ulotka');
                setViewMode('pdf');
              },
            },
            {
              id: 'chpl-45',
              show: showMarkdownTab,
              icon: BookOpen,
              label: t('viewer.tabChpl'),
              secondary: t('viewer.tabChapter'),
              iconClassName: 'translate-y-px',
              onClick: () => {
                setDocumentType('chpl');
                setViewMode('markdown');
              },
            },
          ]
            .filter((tab) => tab.show)
            .map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={tab.onClick}
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-1 rounded px-1.5 py-1 text-[11px] transition-all sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs',
                    isActive
                      ? 'bg-green-50 font-semibold text-green-700 dark:bg-green-950/40 dark:text-green-300'
                      : 'font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-800/40 dark:hover:text-zinc-200',
                  )}
                >
                  <Icon
                    className={cn(
                      'h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5',
                      tab.iconClassName,
                    )}
                  />
                  <span>
                    {tab.label}{' '}
                    <span className="opacity-60">{tab.secondary}</span>
                  </span>
                </button>
              );
            })}
        </div>
      )}

      {/* Panel container */}
      <div
        ref={containerRef}
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-zinc-300 bg-white shadow-[2px_2px_0_0_#d4d4d8] dark:border-zinc-600 dark:bg-zinc-900 dark:shadow-[2px_2px_0_0_#52525b]"
      >
        {/* Header */}
        <div className="group/header flex items-center justify-between gap-3 border-b border-l-4 border-zinc-200 border-l-green-600 bg-zinc-50 px-4 py-2.5 dark:border-zinc-700 dark:border-l-green-500 dark:bg-zinc-800">
          <div className="flex min-w-0 items-center gap-3">
            <div className="min-w-0">
              <div className="flex min-w-0 items-baseline gap-1.5">
                <span className="truncate text-sm font-bold text-zinc-900 dark:text-zinc-100">
                  {quote.drugName}
                </span>
                {quote.activeSubstance && (
                  <span className="truncate text-xs text-zinc-500 dark:text-zinc-400">
                    ({quote.activeSubstance})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                {(quote.strength || quote.form) && (
                  <span className="truncate text-zinc-400 dark:text-zinc-500">
                    {quote.strength && quote.strength !== '-' && (
                      <span>{quote.strength}</span>
                    )}
                    {quote.strength && quote.strength !== '-' && quote.form && (
                      <span className="mx-1">·</span>
                    )}
                    {quote.form && (
                      <span>{formatDrugForm(language, quote.form)}</span>
                    )}
                  </span>
                )}
                {quote.drugId && <CopyDrugId id={quote.drugId} />}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded border-2 border-zinc-800 bg-white text-zinc-600 shadow-[2px_2px_0_0_#27272a] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:bg-red-100 hover:text-red-600 hover:shadow-[1px_1px_0_0_#27272a] dark:border-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:shadow-[2px_2px_0_0_#e4e4e7] dark:hover:bg-red-900/50 dark:hover:text-red-400 dark:hover:shadow-[1px_1px_0_0_#e4e4e7]"
            title={t('viewer.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation controls */}
        {hasMarkdownContent === true &&
          viewMode === 'markdown' &&
          visibleQuoteCount > 0 && (
            <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-2 dark:border-zinc-700 dark:bg-zinc-900">
              <div className="text-zinc-600 dark:text-zinc-300">
                <span className="rounded bg-yellow-200 px-2 py-0.5 text-sm text-zinc-700 dark:bg-yellow-500/20 dark:text-zinc-200">
                  {t('viewer.quoteCounter', {
                    current: activeQuoteIndex + 1,
                    total: visibleQuoteCount,
                  })}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => focusQuoteAtIndex(activeQuoteIndex - 1)}
                  disabled={activeQuoteIndex <= 0}
                  className="inline-flex h-8 items-center gap-1 rounded-md border-2 border-yellow-400 bg-yellow-100 px-2.5 text-xs font-medium text-yellow-900 shadow-[2px_2px_0_0_#ca8a04] transition-all enabled:hover:translate-x-[1px] enabled:hover:translate-y-[1px] enabled:hover:bg-yellow-200 enabled:hover:shadow-[1px_1px_0_0_#ca8a04] enabled:active:translate-x-[2px] enabled:active:translate-y-[2px] enabled:active:shadow-none disabled:opacity-40 disabled:shadow-none dark:border-yellow-400/50 dark:bg-yellow-950 dark:text-yellow-200 dark:shadow-[2px_2px_0_0_rgba(250,204,21,0.4)] dark:enabled:hover:bg-yellow-900 dark:enabled:hover:shadow-[1px_1px_0_0_rgba(250,204,21,0.4)]"
                  title={t('viewer.prevQuoteTitle')}
                >
                  <ChevronUp className="h-5 w-5 text-yellow-700 dark:text-yellow-300" />
                  <span>{t('viewer.prev')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => focusQuoteAtIndex(activeQuoteIndex + 1)}
                  disabled={activeQuoteIndex >= visibleQuoteCount - 1}
                  className="inline-flex h-8 items-center gap-1 rounded-md border-2 border-yellow-400 bg-yellow-100 px-2.5 text-xs font-medium text-yellow-900 shadow-[2px_2px_0_0_#ca8a04] transition-all enabled:hover:translate-x-[1px] enabled:hover:translate-y-[1px] enabled:hover:bg-yellow-200 enabled:hover:shadow-[1px_1px_0_0_#ca8a04] enabled:active:translate-x-[2px] enabled:active:translate-y-[2px] enabled:active:shadow-none disabled:opacity-40 disabled:shadow-none dark:border-yellow-400/50 dark:bg-yellow-950 dark:text-yellow-200 dark:shadow-[2px_2px_0_0_rgba(250,204,21,0.4)] dark:enabled:hover:bg-yellow-900 dark:enabled:hover:shadow-[1px_1px_0_0_rgba(250,204,21,0.4)]"
                  title={t('viewer.nextQuoteTitle')}
                >
                  <ChevronDown className="h-5 w-5 text-yellow-700 dark:text-yellow-300" />
                  <span>{t('viewer.next')}</span>
                </button>
              </div>
            </div>
          )}

        {/* Content */}
        <div className="relative flex-1 overflow-hidden">
          {/* Chapter view */}
          <div
            ref={markdownScrollRef}
            className={cn(
              'h-full overflow-y-auto bg-gradient-to-br from-white via-zinc-50/30 to-white px-6 py-4 dark:from-zinc-900 dark:via-zinc-900/95 dark:to-zinc-900',
              viewMode !== 'markdown' && 'hidden',
            )}
          >
            {isChapter45Loading ? (
              <div className="flex h-full items-center justify-center py-8 text-zinc-500 dark:text-zinc-400">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
                  <span className="text-sm">{t('viewer.loadingChapter')}</span>
                </div>
              </div>
            ) : chapter45Content ? (
              <Chapter45Content
                content={chapter45Content}
                anchors={chapter45Anchors}
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center py-8 text-zinc-500 dark:text-zinc-400">
                <FileText className="mb-3 h-12 w-12 opacity-30" />
                <p>{t('viewer.chapterUnavailable')}</p>
              </div>
            )}
          </div>

          {/* PDF view */}
          <div className={cn('h-full', viewMode !== 'pdf' && 'hidden')}>
            {pdfUrl ? (
              <PdfViewerWithInitialPage
                key={`${pdfUrl}-${initialPdfPage}`}
                fileUrl={pdfUrl}
                initialPage={initialPdfPage}
                downloadUrl={isUlotka ? pdfUrl : quote.url}
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center py-8 text-zinc-500 dark:text-zinc-400">
                <FileText className="mb-3 h-12 w-12 opacity-30" />
                <p>{t('viewer.pdfUnavailable')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Date footer */}
        {(documentUpdatedAt || lastSyncCheckDate) && (
          <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-4 py-1.5 text-[10px] text-zinc-400 sm:text-[11px] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-500">
            {documentUpdatedAt && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-2.5 w-2.5" />
                <span>
                  {t('viewer.lastUpdate')}{' '}
                  <span className="font-medium text-zinc-500 dark:text-zinc-400">
                    <span className="sm:hidden">
                      {formatDateShort(documentUpdatedAt, language)}
                    </span>
                    <span className="hidden sm:inline">
                      {formatDate(documentUpdatedAt, language)}
                    </span>
                  </span>
                </span>
              </span>
            )}
            {lastSyncCheckDate && (
              <span className="ml-auto inline-flex items-center gap-1">
                <RefreshCw className="h-2.5 w-2.5" />
                <span className="sm:hidden">
                  {t('viewer.rplShort')}{' '}
                  <span className="font-medium text-zinc-500 dark:text-zinc-400">
                    {formatDateShort(lastSyncCheckDate, language)}
                  </span>
                </span>
                <span className="hidden sm:inline">
                  {t('viewer.checkedInRpl')}{' '}
                  <span className="font-medium text-zinc-500 dark:text-zinc-400">
                    {formatDate(lastSyncCheckDate, language, {
                      includeTime: true,
                    })}
                  </span>
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Exported ChplViewer Component
// ============================================================================

interface ChplViewerProps {
  quote: ChplQuote;
  onClose: () => void;
  initialViewMode?: ViewMode;
  /** When true, scroll/navigate to chapter 4.5; when false, start at beginning. Default false. */
  scrollToChapter45?: boolean;
  onViewModeChange?: (mode: ViewMode) => void;
  /** Document type: 'chpl' for ChPL (default), 'ulotka' for patient leaflet (PDF-only) */
  documentType?: DocumentType;
  /** Whether this drug has a ChPL PDF available */
  hasChplPdf?: boolean;
  /** Whether this drug has an ulotka (patient leaflet) available */
  hasUlotka?: boolean;
  /** Pre-known extraction availability from drug metadata (avoids waiting for fetch) */
  hasChplExtraction?: boolean;
}

export function ChplViewer({
  quote,
  onClose,
  initialViewMode,
  scrollToChapter45,
  onViewModeChange,
  documentType,
  hasChplPdf,
  hasUlotka,
  hasChplExtraction,
}: ChplViewerProps) {
  return (
    <ChplViewerInner
      quote={quote}
      onClose={onClose}
      initialViewMode={initialViewMode}
      scrollToChapter45={scrollToChapter45}
      onViewModeChange={onViewModeChange}
      documentType={documentType}
      hasChplPdf={hasChplPdf}
      hasUlotka={hasUlotka}
      hasChplExtraction={hasChplExtraction}
    />
  );
}
